/**
 * Ảnh QR MỘT mẫu, không có nút chọn — dùng trên cổng khách và trang booking
 * công khai.
 *
 * Khách tự quét bằng máy của họ nên không cần chọn mẫu, không cần tải hay
 * copy: những nút đó chỉ có nghĩa với nhân sự đang gửi ảnh cho người khác.
 *
 * Vẽ hỏng (mạng rớt lúc tải logo, canvas bị chặn) thì KHÔNG để màn trống mà
 * rơi về `fallback` — mã QR trơn của luồng cũ. Khách đang muốn trả tiền, mất
 * mã là mất tiền; ảnh có thương hiệu chỉ là phần đẹp thêm.
 */

import { type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { BrandLogo } from "./brand";
import { renderPoster, type PosterBankInfo } from "./renderer";
import { DEFAULT_TEMPLATE_ID, type QRTemplateId } from "./templates";

type Props = {
  bank: PosterBankInfo;
  amount: number;
  memo: string;
  logo: BrandLogo;
  /** Mẫu dùng cho cổng khách. Mặc định là mẫu kem cổ điển. */
  templateId?: QRTemplateId;
  /** Mã QR trơn, hiện khi dựng ảnh hỏng hoặc trong lúc chờ dựng. */
  fallback: ReactNode;
  className?: string;
};

export function QRPosterView({
  bank,
  amount,
  memo,
  logo,
  templateId = DEFAULT_TEMPLATE_ID,
  fallback,
  className = "",
}: Props) {
  const posterQ = useQuery({
    queryKey: [
      "qr-poster-single",
      bank.bankBin,
      bank.accountNumber,
      bank.accountHolder,
      amount,
      memo,
      logo,
      templateId,
    ],
    queryFn: () => renderPoster({ bank, content: { amount, memo }, logo }, templateId),
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (!posterQ.data) return <>{fallback}</>;

  return (
    <img
      src={posterQ.data.dataUrl}
      alt="Mã QR thanh toán"
      className={`mx-auto block h-auto w-full max-w-full rounded-xl ${className}`}
    />
  );
}
